import { useRef, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { loadYTApi } from '../utils/youtubeApi';

// Every insertion site in this app already stores `src` as a full
// https://www.youtube.com/embed/{id} URL, but width/height show up
// inconsistently as numbers, bare numeric strings, or "400px" strings
// depending on which insertion path was used — normalize defensively.
const toPx = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const ResizableYoutube = ({ node, updateAttributes }) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  // Suppress onStateChange while we're the one applying an incoming remote
  // sync, so applying it doesn't immediately re-publish it right back.
  const suppressRef = useRef(false);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startWidth = useRef(0);
  const startHeight = useRef(0);

  const width = toPx(node.attrs.width, 560);
  const height = toPx(node.attrs.height, 315);
  const src = node.attrs.src || '';

  // enablejsapi is required for the YT.Player wrapper below to actually be
  // able to attach to and control this iframe.
  const embedSrc = (() => {
    if (!src) return src;
    try {
      const url = new URL(src, window.location.origin);
      if (!url.searchParams.has('enablejsapi')) url.searchParams.set('enablejsapi', '1');
      if (!url.searchParams.has('origin')) url.searchParams.set('origin', window.location.origin);
      return url.toString();
    } catch (_) {
      return src;
    }
  })();

  // Wrap the iframe with the real YouTube IFrame Player API (same approach
  // the whiteboard's YouTubePlayer.jsx already uses successfully) instead
  // of listening for raw postMessage — YouTube only ever talks to a caller
  // that went through this handshake, so a plain `window.addEventListener
  // ('message', ...)` never receives onStateChange at all. Bridges into the
  // same docMediaPublish/docMediaSync events audio/video already use.
  useEffect(() => {
    let destroyed = false;
    loadYTApi().then(() => {
      if (destroyed || !iframeRef.current) return;
      playerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onStateChange: (e) => {
            if (suppressRef.current) return;
            const S = window.YT.PlayerState;
            const t = playerRef.current?.getCurrentTime?.() ?? 0;
            if (e.data === S.PLAYING) {
              window.dispatchEvent(new CustomEvent('docMediaPublish', { detail: { src, action: 'play', currentTime: t } }));
            } else if (e.data === S.PAUSED || e.data === S.ENDED) {
              window.dispatchEvent(new CustomEvent('docMediaPublish', { detail: { src, action: 'pause', currentTime: t } }));
            }
          },
        },
      });
    });

    const onSync = (e) => {
      const { src: eSrc, action, currentTime } = e.detail || {};
      if (eSrc !== src) return;
      const p = playerRef.current;
      if (!p?.playVideo) return;
      suppressRef.current = true;
      try {
        if (typeof currentTime === 'number' && currentTime > 0) p.seekTo(currentTime, true);
        if (action === 'play') p.playVideo();
        else if (action === 'pause') p.pauseVideo();
      } catch (_) {}
      setTimeout(() => { suppressRef.current = false; }, 700);
    };
    window.addEventListener('docMediaSync', onSync);

    return () => {
      destroyed = true;
      window.removeEventListener('docMediaSync', onSync);
      try { playerRef.current?.destroy?.(); } catch (_) {}
      playerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    const container = containerRef.current;
    const iframe = iframeRef.current;
    if (!container || !iframe) return;

    const handleMouseDown = (e) => {
      const isHandle = (e.target.classList && e.target.classList.contains('resize-handle')) ||
        (typeof e.target.closest === 'function' && !!e.target.closest('.resize-handle'));
      if (!isHandle) return;
      e.preventDefault();
      e.stopPropagation();
      isResizing.current = true;
      startX.current = e.clientX;
      startY.current = e.clientY;
      startWidth.current = iframe.offsetWidth;
      startHeight.current = iframe.offsetHeight;
    };

    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      e.preventDefault();
      const dx = e.clientX - startX.current;
      const dy = e.clientY - startY.current;
      let newWidth = Math.max(200, startWidth.current + dx);
      let newHeight = Math.max(120, startHeight.current + dy);

      // Cap growth to the editor column's width — same rule ImageNodeView
      // uses for photos, so resizing a video behaves the same way as
      // resizing an image instead of letting it grow past the page.
      const editorEl = container.closest('.ProseMirror');
      const maxContainerWidth = editorEl ? editorEl.offsetWidth : window.innerWidth;
      if (newWidth > maxContainerWidth) {
        const aspect = startWidth.current / startHeight.current;
        newWidth = maxContainerWidth;
        newHeight = newWidth / aspect;
      }

      updateAttributes({ width: Math.round(newWidth), height: Math.round(newHeight) });
    };

    const handleMouseUp = () => {
      isResizing.current = false;
    };

    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [updateAttributes]);

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 0);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 1);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  return (
    <NodeViewWrapper
      style={{
        margin: '0',
        padding: '0',
        display: 'block'
      }}
      data-type="youtube"
      draggable
      data-drag-handle=""
    >
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div
        ref={containerRef}
        style={{
          margin: '0',
          padding: '0',
          display: 'inline-block',
          position: 'relative'
        }}
      >
        <iframe
          ref={iframeRef}
          src={embedSrc}
          title="YouTube video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{
            margin: '0',
            padding: '0',
            display: 'block',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            // Unlike <img>, an iframe has no intrinsic aspect ratio to fall
            // back on, so plain max-width:100% alone would leave the height
            // stuck at its stored px value while the width shrinks —
            // aspect-ratio + height:auto keeps it proportional, matching
            // how images shrink to fit a narrower window.
            width: `${width}px`,
            maxWidth: '100%',
            aspectRatio: `${width} / ${height}`,
            height: 'auto',
          }}
        />
        <div
          className="resize-handle"
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-25px',
            width: '18px',
            height: '18px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)',
            cursor: 'se-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(0,0,0,0.7)',
            fontSize: '11px',
            fontWeight: 'bold',
            zIndex: 10,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.4) 100%)';
            el.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget;
            el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)';
            el.style.transform = 'scale(1)';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style={{ transform: 'scaleX(-1)', transformOrigin: '50% 50%', pointerEvents: 'none' }}>
            <path fillRule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707"/>
          </svg>
        </div>
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default ResizableYoutube;
